import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    IconButton, LinearProgress, MenuItem, Paper, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
    TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { AuthService } from '../../services/auth.service';
import { ComplianceService } from '../../services/compliance.service';
import { ClosePeriod, CloseTask, TaskStatus, TemplateTask } from '../../interfaces/compliance.interface';
import AttachmentsPanel from '../common/AttachmentsPanel';
import ScrollableTable from '../common/ScrollableTable';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { currentUserIdFromToken, monthLabel, MONTHS, TASK_STATUS_LABEL } from './complianceUtils';

const cell = { flex: '1 1 200px', maxWidth: 320 } as const;
const PERIOD_COLOR: Record<string, 'default' | 'info' | 'success'> = { OPEN: 'default', IN_PROGRESS: 'info', SIGNED_OFF: 'success' };

const CloseChecklist: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const institutionId = selectedInstitutionId || AuthService.getSelectedInstitution() || '';
    const toast = useToast();
    // COMPLIANCE_MANAGE runs the checklist (open, templates, tasks); LEDGER_CLOSE_MANAGE signs off, reopens and sets the gate.
    const canManage = usePermission(PERMISSIONS.COMPLIANCE_MANAGE);
    const canSign = usePermission(PERMISSIONS.LEDGER_CLOSE_MANAGE);
    const me = currentUserIdFromToken();
    const canActOn = (t: CloseTask) => canManage || (!!me && t.ownerUserId === me);
    const [newOwner, setNewOwner] = useState<EmployeeOption | null>(null);
    const today = toLocalIsoDate();

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState(Number(today.slice(0, 4)));
    const [periods, setPeriods] = useState<ClosePeriod[]>([]);
    const [selected, setSelected] = useState<ClosePeriod | null>(null);
    const selectedIdRef = useRef<string | null>(null);
    selectedIdRef.current = selected?.id ?? null;
    const [opening, setOpening] = useState(false);
    const [templates, setTemplates] = useState<TemplateTask[]>([]);
    const [gate, setGate] = useState(false);
    const [openMonth, setOpenMonth] = useState(Number(today.slice(5, 7)));
    const [busy, setBusy] = useState(false);
    const [reopenReason, setReopenReason] = useState('');
    const [reopenOpen, setReopenOpen] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [noteTask, setNoteTask] = useState<CloseTask | null>(null);
    const [naNote, setNaNote] = useState('');
    // One owner dialog serves both a period task and a template task (default tasks start with no owner).
    const [ownerTarget, setOwnerTarget] = useState<{ task?: CloseTask; template?: TemplateTask } | null>(null);
    const [ownerPick, setOwnerPick] = useState<EmployeeOption | null>(null);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const [p, t, g] = await Promise.all([
                ComplianceService.listPeriods(institutionId, year),
                ComplianceService.listTemplates(institutionId),
                ComplianceService.getGate(institutionId),
            ]);
            setPeriods(p); setTemplates(t); setGate(g.requireCloseChecklist);
            // the list rows carry no tasks: re-fetch the open period's detail so its checklist stays current
            const openId = selectedIdRef.current;
            if (openId && p.some(x => x.id === openId)) {
                setSelected(await ComplianceService.getPeriod(openId));
            } else {
                setSelected(null);
            }
        } catch (e) {
            toast.showApiError(e, 'Failed to load close checklist');
        } finally { setLoading(false); }
    }, [institutionId, year]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
        setBusy(true);
        try { await fn(); toast.showSuccess(ok); await load(); }
        catch (e) { toast.showApiError(e, fail); }
        finally { setBusy(false); }
    };

    const openChecklist = async (p: ClosePeriod) => {
        setOpening(true);
        try { setSelected(await ComplianceService.getPeriod(p.id)); }
        catch (e) { toast.showApiError(e, 'Failed to open the checklist'); }
        finally { setOpening(false); }
    };

    const setTaskStatus = (t: CloseTask, status: TaskStatus, note?: string) =>
        run(() => ComplianceService.setCloseTaskStatus(t.id, status, note), 'Task updated', 'Failed to update task');

    const signedOff = selected?.status === 'SIGNED_OFF';

    return (
        <Box sx={{ p: 2 }} data-help-page="closeChecklist.page">
            <Typography variant="h5" gutterBottom>Month-End Close Checklist</Typography>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} data-help="closeChecklist.tabs">
                <Tab label="Periods" /><Tab label="Template and gate" />
            </Tabs>
            {loading ? <CircularProgress /> : tab === 0 ? (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                        <TextField data-help="closeChecklist.year" sx={cell} size="small" type="number" label="Year" value={year} onChange={e => setYear(Number(e.target.value) || year)} />
                        {canManage && (
                            <>
                                <TextField data-help="closeChecklist.openMonth" sx={cell} size="small" select label="Month to open" value={openMonth} onChange={e => setOpenMonth(Number(e.target.value))}>
                                    {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                                </TextField>
                                <Button data-help="closeChecklist.openPeriod" variant="contained" disabled={busy} onClick={() => run(() => ComplianceService.openPeriod(institutionId, year, openMonth), 'Period opened', 'Failed to open period')}>
                                    Open period
                                </Button>
                            </>
                        )}
                    </Box>
                    <Paper variant="outlined" sx={{ mb: 3 }} data-help-section="closeChecklist.periods.table">
                        <ScrollableTable>
                            <Table size="small">
                                <TableHead><TableRow><TableCell data-help="closeChecklist.periods.col.period">Period</TableCell><TableCell data-help="closeChecklist.periods.col.status">Status</TableCell><TableCell data-help="closeChecklist.periods.col.progress">Progress</TableCell><TableCell data-help="closeChecklist.periods.col.requiredOpen">Required open</TableCell><TableCell /></TableRow></TableHead>
                                <TableBody>
                                    {periods.length === 0 && <TableRow><TableCell colSpan={5}>No periods opened for {year}.</TableCell></TableRow>}
                                    {periods.map(p => (
                                        <TableRow key={p.id} hover selected={selected?.id === p.id}>
                                            <TableCell>{monthLabel(p.month)} {p.year}</TableCell>
                                            <TableCell><Chip size="small" color={PERIOD_COLOR[p.status]} label={p.status === 'SIGNED_OFF' ? 'Signed off' : p.status === 'IN_PROGRESS' ? 'In progress' : 'Open'} />{p.reopened && <Chip size="small" sx={{ ml: 1 }} color="warning" label="Reopened" />}</TableCell>
                                            <TableCell sx={{ minWidth: 140 }}>
                                                <LinearProgress variant="determinate" value={p.tasksTotal ? (100 * p.tasksFinished) / p.tasksTotal : 0} />
                                                <Typography variant="caption">{p.tasksFinished} / {p.tasksTotal}</Typography>
                                            </TableCell>
                                            <TableCell>{p.requiredOpen}</TableCell>
                                            <TableCell align="right"><Button data-help="closeChecklist.periods.action.open" size="small" disabled={opening} onClick={() => openChecklist(p)}>Open checklist</Button></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollableTable>
                    </Paper>
                    {selected && (
                        <Paper variant="outlined" sx={{ p: 2 }} data-help-section="closeChecklist.detail">
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" sx={{ flex: '1 1 200px' }}>{monthLabel(selected.month)} {selected.year}</Typography>
                                {canSign && !signedOff && (
                                    <Button data-help="closeChecklist.signOff" variant="contained" disabled={busy || selected.requiredOpen > 0 || selected.tasksTotal === 0}
                                        onClick={() => run(() => ComplianceService.signOffPeriod(selected.id), 'Period signed off', 'Failed to sign off')}>
                                        Sign off
                                    </Button>
                                )}
                                {canSign && signedOff && <Button data-help="closeChecklist.reopenPeriod" color="warning" onClick={() => { setReopenReason(''); setReopenOpen(true); }}>Reopen</Button>}
                            </Box>
                            {!signedOff && selected.requiredOpen > 0 && <Alert severity="info" sx={{ mb: 1 }}>{selected.requiredOpen} required task(s) still open.</Alert>}
                            <ScrollableTable data-help-section="closeChecklist.tasks.table">
                                <Table size="small">
                                    <TableHead><TableRow><TableCell data-help="closeChecklist.tasks.col.task">Task</TableCell><TableCell data-help="closeChecklist.tasks.col.owner">Owner</TableCell><TableCell data-help="closeChecklist.tasks.col.due">Due</TableCell><TableCell data-help="closeChecklist.tasks.col.status">Status</TableCell><TableCell /></TableRow></TableHead>
                                    <TableBody>
                                        {selected.tasks.map(t => (
                                            <TableRow key={t.id}>
                                                <TableCell>
                                                    {t.title}{t.required ? ' *' : ''}
                                                    {t.linkPath && <Button data-help="closeChecklist.tasks.action.go" size="small" component={RouterLink} to={t.linkPath}>Go</Button>}
                                                    {t.note && <Typography variant="caption" display="block">{t.note}</Typography>}
                                                </TableCell>
                                                <TableCell>
                                                    {t.ownerName || t.ownerRole || <Typography component="span" variant="body2" color="text.secondary">Unassigned</Typography>}
                                                    {canManage && !signedOff && <Button data-help="closeChecklist.tasks.action.owner" size="small" onClick={() => { setOwnerPick(null); setOwnerTarget({ task: t }); }}>{t.ownerUserId || t.ownerRole ? 'Change' : 'Assign'}</Button>}
                                                </TableCell>
                                                <TableCell>{t.dueDate ? formatDate(t.dueDate) : ''}</TableCell>
                                                <TableCell><Chip size="small" label={TASK_STATUS_LABEL[t.status]} color={t.status === 'DONE' ? 'success' : t.status === 'NOT_APPLICABLE' ? 'default' : 'warning'} /></TableCell>
                                                <TableCell align="right">
                                                    {!signedOff && canActOn(t) && t.status !== 'DONE' && <Button data-help="closeChecklist.tasks.action.done" size="small" disabled={busy} onClick={() => setTaskStatus(t, 'DONE')}>Done</Button>}
                                                    {!signedOff && canActOn(t) && t.status !== 'NOT_APPLICABLE' && !t.required && <Button data-help="closeChecklist.tasks.action.na" size="small" disabled={busy} onClick={() => setNoteTask(t)}>N/A</Button>}
                                                    {!signedOff && canActOn(t) && t.status !== 'OPEN' && <Button data-help="closeChecklist.tasks.action.reopen" size="small" disabled={busy} onClick={() => setTaskStatus(t, 'OPEN')}>Reopen</Button>}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ScrollableTable>
                            {selected.tasks.length > 0 && (
                                <Box sx={{ mt: 2 }} data-help-section="closeChecklist.evidence">
                                    <Typography variant="subtitle2">Evidence (first task: {selected.tasks[0].title})</Typography>
                                    <AttachmentsPanel entityType="CLOSE_TASK" entityId={selected.tasks[0].id} canManage={(canManage || canSign) && !signedOff} />
                                </Box>
                            )}
                        </Paper>
                    )}
                </>
            ) : (
                <>
                    <FormControlLabel data-help="closeChecklist.gate" control={<Switch checked={gate} disabled={!canSign || busy}
                        onChange={e => run(() => ComplianceService.setGate(institutionId, e.target.checked), 'Gate updated', 'Failed to update gate')} />}
                        label="Require a signed-off checklist before the ledger lock can move forward" />
                    <Box sx={{ my: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {canManage && <Button data-help="closeChecklist.tpl.loadDefaults" variant="outlined" disabled={busy} onClick={async () => {
                            setBusy(true);
                            try {
                                const r = await ComplianceService.loadChecklistDefaults(institutionId);
                                toast.showSuccess(`Loaded ${r.created} task(s), skipped ${r.skipped}`);
                                await load();
                            } catch (e) { toast.showApiError(e, 'Failed to load defaults'); }
                            finally { setBusy(false); }
                        }}>Load default checklist</Button>}
                        {canManage && <>
                            <TextField data-help="closeChecklist.tpl.newTitle" sx={cell} size="small" label="New task title" value={newTitle} onChange={e => setNewTitle(e.target.value)} />
                            <EmployeeAutocomplete label="Owner (optional)" size="small" value={newOwner} data-help="closeChecklist.tpl.newOwner" onChange={setNewOwner} sx={cell} />
                            <Button data-help="closeChecklist.tpl.add" disabled={busy || !newTitle.trim()} onClick={() => run(async () => {
                                await ComplianceService.createTemplate(institutionId, { title: newTitle.trim(), ownerUserId: newOwner?.id ?? null, dueDayOffset: 5, required: true, sequence: (templates.length + 1) * 10 });
                                setNewTitle(''); setNewOwner(null);
                            }, 'Task added', 'Failed to add task')}>Add task</Button>
                        </>}
                    </Box>
                    <Paper variant="outlined" data-help-section="closeChecklist.tpl.table">
                        <ScrollableTable>
                            <Table size="small">
                                <TableHead><TableRow><TableCell data-help="closeChecklist.tpl.col.seq">#</TableCell><TableCell data-help="closeChecklist.tpl.col.task">Task</TableCell><TableCell data-help="closeChecklist.tpl.col.owner">Owner</TableCell><TableCell data-help="closeChecklist.tpl.col.due">Due (days after month end)</TableCell><TableCell data-help="closeChecklist.tpl.col.required">Required</TableCell><TableCell /></TableRow></TableHead>
                                <TableBody>
                                    {templates.length === 0 && <TableRow><TableCell colSpan={6}>No template tasks. Use "Load default checklist".</TableCell></TableRow>}
                                    {templates.map(t => (
                                        <TableRow key={t.id}>
                                            <TableCell>{t.sequence}</TableCell><TableCell>{t.title}</TableCell>
                                            <TableCell>
                                                {t.ownerName || t.ownerRole || <Typography component="span" variant="body2" color="text.secondary">Unassigned</Typography>}
                                                {canManage && <Button data-help="closeChecklist.tpl.action.owner" size="small" onClick={() => { setOwnerPick(null); setOwnerTarget({ template: t }); }}>{t.ownerUserId || t.ownerRole ? 'Change' : 'Assign'}</Button>}
                                            </TableCell>
                                            <TableCell>{t.dueDayOffset}</TableCell><TableCell>{t.required ? 'Yes' : 'No'}</TableCell>
                                            <TableCell align="right">{canManage && <IconButton data-help="closeChecklist.tpl.action.delete" size="small" aria-label="Delete task" onClick={() => run(() => ComplianceService.deleteTemplate(t.id), 'Task removed', 'Failed to remove task')}><DeleteIcon fontSize="small" /></IconButton>}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollableTable>
                    </Paper>
                </>
            )}

            <Dialog open={ownerTarget !== null} onClose={() => setOwnerTarget(null)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'closeChecklist.ownerForm.page' } as any}>
                <DialogTitle>Task owner</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {ownerTarget?.task?.title ?? ownerTarget?.template?.title}
                        {ownerTarget?.template ? ' (applies to periods opened from now on)' : ''}
                    </Typography>
                    <EmployeeAutocomplete label="Owner" size="small" value={ownerPick} onChange={setOwnerPick} data-help="closeChecklist.ownerForm.field.owner" sx={{ width: '100%' }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOwnerTarget(null)} data-help="closeChecklist.ownerForm.cancel">Cancel</Button>
                    <Button variant="contained" disabled={busy} data-help="closeChecklist.ownerForm.save" onClick={async () => {
                        const target = ownerTarget;
                        if (!target) return;
                        const ownerId = ownerPick?.id ?? null;
                        if (target.task) {
                            await run(() => ComplianceService.setCloseTaskOwner(target.task!.id, ownerId), 'Owner saved', 'Failed to save the owner');
                        } else if (target.template) {
                            const t = target.template;
                            await run(() => ComplianceService.updateTemplate(t.id, {
                                title: t.title, ownerUserId: ownerId, ownerRole: ownerId ? null : t.ownerRole ?? null,
                                dueDayOffset: t.dueDayOffset, required: t.required, linkPath: t.linkPath ?? null, sequence: t.sequence,
                            }), 'Owner saved', 'Failed to save the owner');
                        }
                        setOwnerTarget(null);
                    }}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={reopenOpen} onClose={() => setReopenOpen(false)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'closeChecklist.reopenForm.page' } as any}>
                <DialogTitle>Reopen period</DialogTitle>
                <DialogContent sx={{ pt: 2 }}><TextField label="Reason" required fullWidth multiline value={reopenReason} data-help="closeChecklist.reopenForm.field.reason" onChange={e => setReopenReason(e.target.value)} /></DialogContent>
                <DialogActions>
                    <Button onClick={() => setReopenOpen(false)} data-help="closeChecklist.reopenForm.cancel">Cancel</Button>
                    <Button variant="contained" color="warning" disabled={busy || !reopenReason.trim()} data-help="closeChecklist.reopenForm.confirm" onClick={async () => {
                        if (!selected) return;
                        await run(() => ComplianceService.reopenPeriod(selected.id, reopenReason.trim()), 'Period reopened', 'Failed to reopen');
                        setReopenOpen(false);
                    }}>Reopen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!noteTask} onClose={() => setNoteTask(null)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'closeChecklist.naForm.page' } as any}>
                <DialogTitle>Mark not applicable</DialogTitle>
                <DialogContent sx={{ pt: 2 }}><TextField label="Note" fullWidth multiline value={naNote} data-help="closeChecklist.naForm.field.note" onChange={e => setNaNote(e.target.value)} /></DialogContent>
                <DialogActions>
                    <Button onClick={() => setNoteTask(null)} data-help="closeChecklist.naForm.cancel">Cancel</Button>
                    <Button variant="contained" data-help="closeChecklist.naForm.confirm" onClick={async () => {
                        if (noteTask) await setTaskStatus(noteTask, 'NOT_APPLICABLE', naNote.trim());
                        setNoteTask(null); setNaNote('');
                    }}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CloseChecklist;
